/**
 * `/training/managers`: the static page "What your team will see" for managers (MA-05).
 * Typed content from `content/managers.ts`, rendered with the training's text blocks. No
 * progress is recorded; the page is linked from the dashboard start card and from
 * `/training`. The server does not gate it: a sales person who opens it only reads what
 * their manager reads. The link to the training uptake shows for `see_team` only (admins,
 * the product owner, 15 Oct 2026).
 */

import { ArrowRight, Icon } from "@norican/icons";
import { Card, PageHeader, Typography } from "@norican/react";
import { Link } from "react-router";

import { DocumentTitle } from "@/components/document-title";
import { useMe } from "@/lib/api";
import { hasCapability } from "@/lib/areas";

import { MANAGER_BRIEF } from "./content";
import { Blocks, plainText, Text } from "./player/text";

export const MANAGERS_PATH = "/training/managers";

export function TrainingManagersPage() {
  const brief = MANAGER_BRIEF;
  const { data: me } = useMe();
  const seeTeam = hasCapability(me, "see_team");
  return (
    <div className="mx-auto flex w-full max-w-4xl min-w-0 flex-col gap-4" data-managers-page>
      <DocumentTitle title={plainText(brief.title)} />
      <PageHeader title={plainText(brief.title)} subtitle={<Text value={brief.subtitle} />} />
      <nav aria-label="On this page" className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {brief.sections.map((section) => (
          <a key={section.id} href={`#${section.id}`} className="text-accent hover:underline">
            {plainText(section.title)}
          </a>
        ))}
      </nav>
      {brief.sections.map((section) => (
        <Card
          key={section.id}
          variant="outline"
          padding="md"
          className="flex min-w-0 scroll-mt-24 flex-col gap-2"
          id={section.id}
        >
          <Typography as="h2" variant="heading-6" className="break-words">
            <Text value={section.title} />
          </Typography>
          <Blocks blocks={section.body} />
        </Card>
      ))}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <Link to="/training" className="text-accent inline-flex items-center gap-1 font-semibold">
          The full training <Icon icon={ArrowRight} size="xs" />
        </Link>
        {seeTeam && (
          <Link
            to="/training/team"
            className="text-accent inline-flex items-center gap-1 font-semibold"
          >
            Training uptake of your team <Icon icon={ArrowRight} size="xs" />
          </Link>
        )}
        <Link
          to="/rules/countries"
          className="text-accent inline-flex items-center gap-1 font-semibold"
        >
          Rules and countries <Icon icon={ArrowRight} size="xs" />
        </Link>
      </div>
    </div>
  );
}
